import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {BsApiService } from '@brightside-web/desktop/data-access/core-services';
import * as ServiceNotificationsActions from './service-notifications.actions';
import { mergeMap, map, catchError, debounceTime, delay } from 'rxjs/operators';
import { concat, filter, of } from 'rxjs';
import { ServiceNotificationItem } from './service-notifications.models';

@Injectable()
export class ServiceNotificationsEffects {
  constructor(
    private actions$: Actions,
    private bsApi: BsApiService
  ) {}

  loadServiceNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceNotificationsActions.loadServiceNotifications),
      mergeMap(() =>
        this.bsApi.get<{ payload: { notifications: ServiceNotificationItem[] } }>(
          'api-mobile',
          '/service-notifications'
        ).pipe(
          debounceTime(500),
          map((response) => {
            const notifications = response.payload.notifications;
            if (!notifications || notifications.length === 0) {
              return ServiceNotificationsActions.loadServiceNotificationsFailure({
                error: 'No notifications available',
              });
            }
            return ServiceNotificationsActions.loadServiceNotificationsSuccess({ notifications });
          }),
          catchError((error) =>
            of(ServiceNotificationsActions.loadServiceNotificationsFailure({ error }))
          )
        )
      )
    )
  );

  patchNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceNotificationsActions.patchServiceNotifications),
      mergeMap(({ updates, retry }) => {
        // Dispatch the optimistic success update immediately
        const optimisticUpdate = of(
          ServiceNotificationsActions.patchServiceNotificationsSuccess({ updates })
        );

        const apiCall = this.bsApi.patch(
          'api-mobile',
          '/service-notifications',
          { notifications: updates }
        ).pipe(
          map(() => ({ type: '[NOOP]' })),
          catchError(error =>
            of(ServiceNotificationsActions.patchServiceNotificationsFailure({ error, updates, retry }))
          )
        );

        return concat(optimisticUpdate, apiCall);
      })
    )
  );

  retryPatchOnFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceNotificationsActions.patchServiceNotificationsFailure),
      filter(({ retry }) => retry !== true), // ✅ only retry if not already marked
      delay(5000),
      map(({ updates }) =>
        ServiceNotificationsActions.patchServiceNotifications({ updates, retry: true })
      )
    )
  );

}
